import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, FileDown, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import TaskRow, { PRIORITY_META } from "../components/TaskRow";
import TaskForm from "../components/TaskForm";
import AgendaItem from "../components/AgendaItem";
import AgendaLegend from "../components/AgendaLegend";
import RescheduleConfirmDialog from "../components/RescheduleConfirmDialog";
import Tip from "../components/Tip";
import EndOfDayBanner from "../components/EndOfDayBanner";
import { formatDate } from "../lib/format";
import { expandTasksInRange } from "../lib/expandRecurrence";
import useToday from "../hooks/useToday";

// Guardrail navigazione: consentiamo ±5 anni dal periodo corrente. Impedisce
// che un click accidentale su "prev/next" migliaia di volte porti l'utente
// nel 3025 (o negli anni '20) — Piera l'ha vista succedere durante il triage.
const YEAR_GUARD_RANGE = 5;
function withinYearGuard(candidate, today) {
  const y = candidate.getFullYear();
  const tY = today.getFullYear();
  return y >= tY - YEAR_GUARD_RANGE && y <= tY + YEAR_GUARD_RANGE;
}

// Dedup del toast "Rete assente": segue Piera che stacca il Wi-Fi in metro e
// vede 30 toast identici in un colpo. Ricorda l'ultima notifica di rete e la
// silenzia per 60s.
let _lastNetworkToastAt = 0;
function notifyNetworkError() {
  const now = Date.now();
  if (now - _lastNetworkToastAt < 60_000) return;
  _lastNetworkToastAt = now;
  toast.error("Rete assente o errore di rete. Riprova.");
}

const VIEWS = [
  { v: "day", label: "La mia giornata" },
  { v: "week", label: "La mia settimana" },
  { v: "month", label: "Il mio mese" },
  { v: "year", label: "Il mio anno" },
];

const GROUP_KEY_PREFIX = "pxos.grouping.agenda.";
const SHOW_COMPLETED_KEY = "pxos.agenda.showCompleted";

const DAY_LABELS_LONG = ["Lunedì","Martedì","Mercoledì","Giovedì","Venerdì","Sabato","Domenica"];
const DAY_LABELS_SHORT = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
const MONTHS_IT = [
  "gennaio","febbraio","marzo","aprile","maggio","giugno",
  "luglio","agosto","settembre","ottobre","novembre","dicembre",
];

// `isoDate` MUST return the YYYY-MM-DD of the LOCAL date, not the UTC date.
// Piera's browser runs in Europe/Rome (UTC+1/+2), so calling toISOString()
// on a `new Date('YYYY-MM-DDT00:00:00')` — which is *local* midnight — yields
// the previous UTC day. That off-by-one propagates into api.agenda(),
// setRef(shiftDate(...)), and the PDF export URLs (RCA in iter22).
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseIso(s) { return s ? new Date(s + "T00:00:00") : new Date(); }
function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function shiftDate(view, ref, dir) {
  const d = new Date(ref);
  if (view === "day") d.setDate(d.getDate() + dir);
  else if (view === "week") d.setDate(d.getDate() + 7 * dir);
  else if (view === "month") d.setMonth(d.getMonth() + dir);
  else d.setFullYear(d.getFullYear() + dir);
  return d;
}

// Restituisce il lunedì della settimana ISO in cui cade `d`.
function mondayOf(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0 = Mon
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

// Titolo prominente centrale — formato italiano naturale per ogni vista.
// Rispetta la forma richiesta da Piera: "Settimana dal X al Y … 2026"
// (preposizione "dal … al …", non "del X–Y", per farla suonare parlato).
function periodTitle(view, ref) {
  if (view === "day") {
    const wd = DAY_LABELS_LONG[(ref.getDay() + 6) % 7];
    return `${wd} ${ref.getDate()} ${MONTHS_IT[ref.getMonth()]} ${ref.getFullYear()}`;
  }
  if (view === "week") {
    const start = mondayOf(ref);
    const end = new Date(start); end.setDate(end.getDate() + 6);
    const sameMonth = start.getMonth() === end.getMonth();
    const sameYear = start.getFullYear() === end.getFullYear();
    if (sameMonth) return `Settimana dal ${start.getDate()} al ${end.getDate()} ${MONTHS_IT[start.getMonth()]} ${start.getFullYear()}`;
    if (sameYear) return `Settimana dal ${start.getDate()} ${MONTHS_IT[start.getMonth()]} al ${end.getDate()} ${MONTHS_IT[end.getMonth()]} ${start.getFullYear()}`;
    return `Settimana dal ${start.getDate()} ${MONTHS_IT[start.getMonth()]} ${start.getFullYear()} al ${end.getDate()} ${MONTHS_IT[end.getMonth()]} ${end.getFullYear()}`;
  }
  if (view === "month") return `${MONTHS_IT[ref.getMonth()].replace(/^./, (c) => c.toUpperCase())} ${ref.getFullYear()}`;
  return `${ref.getFullYear()}`;
}

// Verifica se il ref è nel periodo corrente della vista.
// `today` è iniettato dall'hook `useToday` così il flag si ricalcola
// automaticamente allo scoccare della mezzanotte (Piera lascia l'app
// aperta di notte: alle 00:00:01 il pulsante "Oggi" deve riapparire
// SENZA che l'utente faccia refresh).
function isPeriodCurrent(view, ref, today) {
  if (view === "day") return sameDay(ref, today);
  if (view === "week") return sameDay(mondayOf(ref), mondayOf(today));
  if (view === "month") return ref.getFullYear() === today.getFullYear() && ref.getMonth() === today.getMonth();
  return ref.getFullYear() === today.getFullYear();
}

const TODAY_TOOLTIP = {
  day: "Torna alla giornata di oggi",
  week: "Torna alla settimana corrente",
  month: "Torna al mese corrente",
  year: "Torna all'anno corrente",
};

export default function Agenda() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || "day";
  // Nuovo schema per-view (share link parlanti); manteniamo `ref` come
  // compat legacy per non rompere i vecchi bookmark salvati da Piera.
  const legacyRef = params.get("ref");
  const dateParam = params.get("date");
  const weekStartParam = params.get("week_start");
  const monthParam = params.get("month");  // "YYYY-MM"
  const yearParam = params.get("year");    // "YYYY"
  const areaFilter = (() => {
    const raw = params.get("area") || "";
    // Accept only UUIDs to prevent bogus values from producing 400 spam.
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw) ? raw : "";
  })();
  const { today, dayKey } = useToday();

  // Ricostruisce `ref` in base al param specifico della vista. Se il param
  // fornito è invalido, cade su `today` e mostra un toast italiano — MAI
  // crash o schermata vuota (RCA: Piera che apre un link con `date=null`).
  const [invalidDateNoted, setInvalidDateNoted] = useState(false);
  const ref = useMemo(() => {
    const tryParse = (s) => {
      if (!s) return null;
      const d = parseIso(s);
      return isNaN(d.getTime()) ? null : d;
    };
    let r = null;
    if (view === "day")   r = tryParse(dateParam);
    if (view === "week")  r = tryParse(weekStartParam);
    if (view === "month" && monthParam && /^\d{4}-\d{2}$/.test(monthParam))
      r = tryParse(`${monthParam}-01`);
    if (view === "year"  && yearParam  && /^\d{4}$/.test(yearParam))
      r = new Date(parseInt(yearParam, 10), 0, 1);
    // Fallback legacy `ref`
    if (!r) r = tryParse(legacyRef);
    if (!r) return today;
    return r;
  }, [view, dateParam, weekStartParam, monthParam, yearParam, legacyRef, today, dayKey]);

  useEffect(() => {
    // Se l'utente ha passato un param invalido per la vista corrente,
    // segnalalo una volta e reindirizza a "oggi" nella stessa vista.
    const invalid = (view === "day" && dateParam && isNaN(parseIso(dateParam).getTime())) ||
                    (view === "week" && weekStartParam && isNaN(parseIso(weekStartParam).getTime())) ||
                    (view === "month" && monthParam && !/^\d{4}-\d{2}$/.test(monthParam)) ||
                    (view === "year" && yearParam && !/^\d{4}$/.test(yearParam));
    if (invalid && !invalidDateNoted) {
      toast.message("Data non valida nell'URL. Torno a oggi.");
      setInvalidDateNoted(true);
      writeRef(view, today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, dateParam, weekStartParam, monthParam, yearParam]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heat, setHeat] = useState(null);
  const [areas, setAreas] = useState([]);
  const [group, setGroup] = useState(() => { try { return localStorage.getItem(GROUP_KEY_PREFIX + view) || "chronological"; } catch { return "chronological"; } });
  const [showCompleted, setShowCompleted] = useState(() => { try { return localStorage.getItem(SHOW_COMPLETED_KEY) === "1"; } catch { return false; } });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);

  useEffect(() => { try { localStorage.setItem(GROUP_KEY_PREFIX + view, group); } catch {} }, [group, view]);
  useEffect(() => { try { localStorage.setItem(SHOW_COMPLETED_KEY, showCompleted ? "1" : "0"); } catch {} }, [showCompleted]);

  // Aborta la richiesta agenda precedente quando l'utente clicca prev/next
  // rapidamente — evita che una risposta di 3 click fa arrivi in ritardo
  // e sovrascriva quella corrente. AbortController pulito, no race.
  const inflight = useRef(null);
  const load = async () => {
    if (inflight.current) inflight.current.abort();
    const controller = new AbortController();
    inflight.current = controller;
    setLoading(true);
    try {
      const rf = isoDate(ref);
      const [d, a] = await Promise.all([
        api.agenda(view, rf, showCompleted, { signal: controller.signal, area_id: areaFilter || undefined }),
        api.listAreas({ signal: controller.signal }),
      ]);
      // Se durante il fetch l'utente ha cambiato periodo, questa risposta
      // non è più valida — l'abort l'ha già interrotta ma questa guardia
      // è una cintura di sicurezza.
      if (controller.signal.aborted) return;
      setData(d); setAreas(a);
      if (view === "year") setHeat(await api.yearHeatmap(ref.getFullYear(), { signal: controller.signal }));
      else setHeat(null);
    } catch (e) {
      if (e?.name === "AbortError" || e?.code === "ERR_CANCELED") return;
      notifyNetworkError();
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };
  // Ricarica anche quando il giorno cambia (mezzanotte) e non c'è ref esplicito,
  // così le viste "Oggi" e "Questa settimana" restano allineate al nuovo giorno.
  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [view, dateParam, weekStartParam, monthParam, yearParam, legacyRef, showCompleted, dayKey, areaFilter]);

  // Scrittura URL — normalizza al param specifico della vista.
  const writeRef = useCallback((v, d) => {
    const p = new URLSearchParams();
    p.set("view", v);
    if (v === "day")   p.set("date", isoDate(d));
    if (v === "week")  p.set("week_start", isoDate(mondayOf(d)));
    if (v === "month") p.set("month", `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`);
    if (v === "year")  p.set("year", String(d.getFullYear()));
    if (areaFilter) p.set("area", areaFilter);
    setParams(p);
  }, [setParams, areaFilter]);

  const setView = (v) => writeRef(v, ref);
  const setRef = useCallback((d) => writeRef(view, d), [writeRef, view]);
  const goPrev = useCallback(() => {
    const candidate = shiftDate(view, ref, -1);
    if (!withinYearGuard(candidate, today)) {
      toast.message(`Navigazione oltre ±${YEAR_GUARD_RANGE} anni non consentita.`);
      return;
    }
    setRef(candidate);
  }, [setRef, view, ref, today]);
  const goNext = useCallback(() => {
    const candidate = shiftDate(view, ref, +1);
    if (!withinYearGuard(candidate, today)) {
      toast.message(`Navigazione oltre ±${YEAR_GUARD_RANGE} anni non consentita.`);
      return;
    }
    setRef(candidate);
  }, [setRef, view, ref, today]);
  const goToday = useCallback(() => setRef(today), [setRef, today]);
  const setAreaFilter = useCallback((id) => {
    const p = new URLSearchParams(params);
    if (id) p.set("area", id); else p.delete("area");
    setParams(p);
  }, [params, setParams]);

  // Flag "siamo nel periodo corrente?" — dipende da `dayKey` (giorno di Rome)
  // così a mezzanotte diventa false anche se l'utente non ha toccato nulla,
  // rivelando di nuovo il pulsante "Oggi" senza refresh.
  const currentPeriod = useMemo(
    () => isPeriodCurrent(view, ref, today),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view, dateParam, weekStartParam, monthParam, yearParam, legacyRef, dayKey]
  );

  // Keyboard shortcuts — ← / → shift periodo, T = oggi, D/W/M/Y switch view.
  // Guarded per: input testuali, contenteditable, tasti modificatori,
  // dialog Radix aperti (ricerca dei ruoli aria-modal="true" nel DOM).
  useEffect(() => {
    const isTypingTarget = (t) =>
      !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
    const dialogOpen = () => !!document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [aria-modal="true"]');
    const onKey = (e) => {
      if (isTypingTarget(e.target) || dialogOpen()) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key) {
        case "ArrowLeft":  e.preventDefault(); goPrev(); break;
        case "ArrowRight": e.preventDefault(); goNext(); break;
        case "t": case "T": e.preventDefault(); goToday(); break;
        case "d": case "D": e.preventDefault(); setView("day"); break;
        case "w": case "W": e.preventDefault(); setView("week"); break;
        case "m": case "M": e.preventDefault(); setView("month"); break;
        case "y": case "Y": e.preventDefault(); setView("year"); break;
        default: break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, dateParam, weekStartParam, monthParam, yearParam, legacyRef, dayKey]);

  const areaById = useMemo(() => Object.fromEntries(areas.map((a) => [a.id, a])), [areas]);

  const openEdit = (t) => { setEditing(t); setFormOpen(true); };
  const openNew = (isoDay) => { setPrefillDate(isoDay || null); setEditing(null); setFormOpen(true); };

  // Task Cicli · Fase 1 — reschedule w/ shadcn AlertDialog confirmation
  const [rescheduleReq, setRescheduleReq] = useState(null); // { task, toDate, isOverdueDrop }

  const requestReschedule = (taskId, isoDay, dueIso) => {
    const task = data?.tasks?.find((t) => t.id === taskId);
    if (!task) return;
    const isOverdueDrop = !!(dueIso && isoDay > dueIso);
    setRescheduleReq({ task, toDate: isoDay, isOverdueDrop });
  };

  const confirmReschedule = async () => {
    if (!rescheduleReq) return;
    const { task, toDate } = rescheduleReq;
    setRescheduleReq(null);
    try {
      await api.scheduleTask(task.id, toDate);
      const [Y, M, D] = toDate.split("-");
      toast.success(`Programmato per il ${D}/${M}/${Y}`);
      load();
    } catch { toast.error("Errore riprogrammazione"); }
  };

  const rangeLabel = data ? `${formatDate(data.range.start)} — ${formatDate(data.range.end)}` : "";

  // Separate overdue from in-range tasks for day view
  const { overdueTasks, todayTasks } = useMemo(() => {
    if (!data) return { overdueTasks: [], todayTasks: [] };
    const overdue = [], today = [];
    for (const t of data.tasks) (t.is_overdue ? overdue : today).push(t);
    return { overdueTasks: overdue, todayTasks: today };
  }, [data]);

  // Group tasks for day view (excluding overdue which is shown separately)
  const grouped = useMemo(() => {
    if (!data || view !== "day") return [];
    const groups = {};
    for (const t of todayTasks) {
      let key, label, order;
      if (group === "priority") {
        key = t.priority; label = PRIORITY_META[key]?.label || key;
        order = { critical: 0, high: 1, medium: 2, low: 3 }[key] ?? 9;
      } else if (group === "area") {
        key = t.area_id || "no-area";
        label = t.area_id ? (areaById[t.area_id]?.name || "?") : "Senza area";
        order = t.area_id ? (areaById[t.area_id]?.sort_order ?? 99) : 999;
      } else {
        key = "all"; label = "Cicli"; order = 0;
      }
      groups[key] ??= { label, tasks: [], order };
      groups[key].tasks.push(t);
    }
    return Object.values(groups).sort((a, b) => (a.order > b.order ? 1 : -1));
  }, [todayTasks, group, areaById, view, data]);

  const weekMonday = view === "week" && data ? parseIso(data.range.start) : null;

  // Bug 5/50 — espande le RRULE in occorrenze virtuali per week/month view.
  // Backend restituisce la master (con la sua data-base) più le eventuali
  // next-occurrence già materializzate; qui replichiamo la serie SOLO ai fini
  // della visualizzazione calendario, senza inviare nulla al DB e senza
  // incrementare Cicli aperti (cicli_summary lavora su record reali).
  const expandedTasks = useMemo(() => {
    if (!data || !Array.isArray(data.tasks)) return [];
    if (view !== "week" && view !== "month") return data.tasks;
    const startIso = data.range?.start;
    const endIso = data.range?.end;
    if (!startIso || !endIso) return data.tasks;
    return expandTasksInRange(data.tasks, startIso, endIso);
  }, [data, view]);

  const complete = async (t) => {
    try {
      const res = await api.completeTask(t.id);
      if (res.next_occurrence) toast.success(`✓ Completato — Prossima: ${new Date(res.next_occurrence).toLocaleDateString("it-IT")}`);
      else toast.success("✓ Completato");
      load();
    } catch { toast.error("Errore"); }
  };
  const trash = async (t) => { if (!confirm("Sposta nel cestino?")) return; try { await api.trashTask(t.id); load(); } catch {} };
  const archive = async (t) => { try { await api.archiveTask(t.id); load(); } catch {} };

  return (
    <div className="space-y-5" data-testid="agenda-page">
      <EndOfDayBanner onResolved={() => load()} />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Agenda</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold" data-testid="agenda-title">
            {VIEWS.find((v) => v.v === view)?.label}
          </h1>
          <p className="mt-2 text-sm opacity-70" data-testid="agenda-range">{rangeLabel}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {view === "day" && (
            <Tip label="Esporta la giornata in PDF">
              <a href={api.reportDailyPdfUrl(isoDate(ref))} target="_blank" rel="noreferrer"
                 className="rounded-full border px-3 py-1.5 text-sm inline-flex items-center gap-1" data-testid="agenda-daily-pdf"><FileDown size={14}/> PDF Giornata</a>
            </Tip>
          )}
          {view === "week" && (
            <Tip label="Esporta la settimana in PDF">
              <a href={api.reportWeeklyPdfUrl(isoDate(weekMonday || ref))} target="_blank" rel="noreferrer"
                 className="rounded-full border px-3 py-1.5 text-sm inline-flex items-center gap-1" data-testid="agenda-weekly-pdf"><FileDown size={14}/> PDF Settimana</a>
            </Tip>
          )}
          <Tip label="Crea un nuovo ciclo">
            <button className="pxos-btn-primary text-sm" onClick={() => openNew()} data-testid="agenda-new-task"><Plus size={14}/> Nuovo</button>
          </Tip>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex rounded-full border overflow-hidden" data-testid="agenda-view-switch">
          {VIEWS.map((v) => (
            <Tip key={v.v} label={`Passa alla vista: ${v.label}`}>
              <button onClick={() => setView(v.v)}
                      className={`px-4 py-2 text-sm min-h-[44px] ${view === v.v ? "bg-white shadow-inner font-semibold" : "opacity-70 hover:bg-white/70"}`}
                      style={{ color: "var(--pxos-text-dark)" }} data-testid={`agenda-view-${v.v}`}>
                {v.label}
              </button>
            </Tip>
          ))}
        </div>
      </div>

      {/* Barra navigazione temporale prominente — touch target 44×44 mobile,
          layout a 2 righe automatico sotto 360px (iPhone SE portrait). */}
      <div
        className="flex items-center justify-between gap-2 rounded-2xl border bg-white px-2 py-2 sm:px-4 sm:py-3"
        style={{ borderColor: "var(--pxos-border)" }}
        data-testid="agenda-nav-bar"
      >
        <Tip label={view === "day" ? "Giorno precedente" : view === "week" ? "Settimana precedente" : view === "month" ? "Mese precedente" : "Anno precedente"} side="bottom">
          <button
            onClick={goPrev}
            className="grid place-items-center rounded-full hover:bg-black/5 transition min-w-[44px] min-h-[44px]"
            data-testid="agenda-prev"
            aria-label="Periodo precedente"
          >
            <ChevronLeft size={22} style={{ color: "var(--pxos-text-dark)" }} />
          </button>
        </Tip>
        <div
          className="flex-1 text-center font-display text-sm sm:text-lg font-semibold px-1 sm:px-2 leading-snug break-words"
          style={{ color: "var(--pxos-text-dark)" }}
          data-testid="agenda-period-title"
          aria-live="polite"
        >
          {periodTitle(view, ref)}
        </div>
        <div className="flex items-center gap-1">
          {!currentPeriod && (
            <Tip label={TODAY_TOOLTIP[view]} side="bottom">
              <button
                onClick={goToday}
                className="rounded-full border px-3 text-xs font-semibold hover:bg-black/5 transition min-h-[44px] min-w-[44px]"
                style={{ color: "var(--pxos-text-dark)", borderColor: "var(--pxos-border)" }}
                data-testid="agenda-today"
                aria-label="Torna al periodo corrente"
              >
                Oggi
              </button>
            </Tip>
          )}
          <Tip label={view === "day" ? "Giorno successivo" : view === "week" ? "Settimana successiva" : view === "month" ? "Mese successivo" : "Anno successivo"} side="bottom">
            <button
              onClick={goNext}
              className="grid place-items-center rounded-full hover:bg-black/5 transition min-w-[44px] min-h-[44px]"
              data-testid="agenda-next"
              aria-label="Periodo successivo"
            >
              <ChevronRight size={22} style={{ color: "var(--pxos-text-dark)" }} />
            </button>
          </Tip>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <AgendaLegend />
        <div className="flex items-center gap-3 flex-wrap">
          {areas.length > 0 && (
            <label className="text-xs inline-flex items-center gap-1.5" title="Filtra per area">
              <span className="opacity-70">Area:</span>
              <select
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                className="rounded-lg border px-2 py-1 text-xs min-h-[32px]"
                data-testid="agenda-area-filter"
              >
                <option value="">Tutte</option>
                {areas.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </label>
          )}
          <label className="text-xs inline-flex items-center gap-1.5 cursor-pointer select-none"
                 title={showCompleted ? "Nascondi cicli completati" : "Mostra anche cicli completati"}
                 data-testid="agenda-toggle-completed-wrap">
            <input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} data-testid="agenda-toggle-completed" />
            <span>Mostra completate</span>
          </label>
        </div>
      </div>

      {loading && !data && (
        <div className="space-y-3" data-testid="agenda-skeleton">
          <div className="pxos-card p-3 animate-pulse">
            <div className="h-3 w-24 bg-black/10 rounded mb-3"></div>
            <div className="h-6 bg-black/5 rounded mb-2"></div>
            <div className="h-6 bg-black/5 rounded mb-2"></div>
            <div className="h-6 bg-black/5 rounded"></div>
          </div>
        </div>
      )}

      {view === "day" && (
        <div className="flex items-center justify-end">
          <div className="inline-flex rounded-full border overflow-hidden" data-testid="agenda-grouping">
            {[{ v: "area", label: "Per area" }, { v: "priority", label: "Per priorità" }, { v: "chronological", label: "Cronologico" }].map((g) => (
              <Tip key={g.v} label={`Raggruppa: ${g.label}`}>
                <button onClick={() => setGroup(g.v)}
                        className={`px-3 py-1.5 text-xs font-semibold ${group === g.v ? "bg-white shadow-inner" : "opacity-70 hover:bg-white/70"}`}
                        style={{ color: "var(--pxos-text-dark)" }} data-testid={`agenda-group-${g.v}`}>
                  {g.label}
                </button>
              </Tip>
            ))}
          </div>
        </div>
      )}

      {view === "day" && (
        <>
          {overdueTasks.length > 0 && (
            <section className="pxos-card p-3 border-2" style={{ borderColor: "var(--pxos-type-overdue)", background: "color-mix(in srgb, var(--pxos-type-overdue) 6%, #FFFFFF)" }} data-testid="agenda-overdue-section">
              <h2 className="font-display text-base font-semibold px-2 py-1 inline-flex items-center gap-2" data-testid="agenda-overdue-title" style={{ color: "var(--pxos-type-overdue)" }}>
                <AlertTriangle size={16}/> Scaduti · <span className="text-sm opacity-80">{overdueTasks.length}</span>
              </h2>
              <div className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
                {overdueTasks.map((t) => (
                  <TaskRow key={t.id} task={t} area={areaById[t.area_id]}
                    onComplete={complete} onEdit={openEdit} onArchive={archive} onDelete={trash} />
                ))}
              </div>
            </section>
          )}
          {grouped.length === 0 && overdueTasks.length === 0 ? (
            <div className="pxos-card p-10 text-center opacity-70" data-testid="agenda-day-empty">
              {currentPeriod ? "Nessuna attività per oggi." : "Nessuna attività per questa giornata."}
            </div>
          ) : (
            <div className="space-y-4">
              {grouped.map((g) => (
                <section key={g.label} className="pxos-card p-3" data-testid="agenda-group">
                  <h2 className="font-display text-base font-semibold px-2 py-1 capitalize" data-testid="agenda-group-label">{g.label}</h2>
                  <div className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
                    {g.tasks.map((t) => (
                      <TaskRow key={t.id} task={t} area={areaById[t.area_id]}
                        onComplete={complete} onEdit={openEdit}
                        onArchive={archive} onDelete={trash} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      {view === "week" && weekMonday && (
        <WeekView monday={weekMonday} tasks={expandedTasks} areaById={areaById} today={today}
                  onAddClick={openNew} onDrop={requestReschedule}
                  onDayOpen={(iso) => writeRef("day", parseIso(iso))}
                  onTaskClick={openEdit} />
      )}

      {view === "month" && data && (
        <MonthView refDate={ref} tasks={expandedTasks} areaById={areaById} today={today}
                   onAddClick={openNew} onDrop={requestReschedule}
                   onDayOpen={(iso) => writeRef("day", parseIso(iso))}
                   onTaskClick={openEdit} />
      )}

      {view === "year" && (
        <YearView heat={heat} year={ref.getFullYear()} today={today}
                  onDay={(d) => writeRef("day", parseIso(d))}
                  onMonth={(m) => writeRef("month", new Date(ref.getFullYear(), m - 1, 1))} />
      )}

      <TaskForm open={formOpen} initial={editing} prefillScheduledDate={prefillDate}
                onClose={() => { setFormOpen(false); setPrefillDate(null); }}
                onSaved={load} />

      <RescheduleConfirmDialog
        open={!!rescheduleReq}
        task={rescheduleReq?.task}
        toDate={rescheduleReq?.toDate}
        toTime={null}
        isOverdueDrop={rescheduleReq?.isOverdueDrop}
        onCancel={() => setRescheduleReq(null)}
        onConfirm={confirmReschedule}
      />
    </div>
  );
}

// ─── Week View · griglia 7 giorni con drop targets ──────────────
function WeekView({ monday, tasks, areaById, today, onAddClick, onDrop, onDayOpen, onTaskClick }) {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(d.getDate() + i); return d;
  });

  const byDay = useMemo(() => {
    const g = {};
    for (const t of tasks) {
      const key = t.scheduled_date || t.due_date;
      if (!key) continue;
      g[key] ??= [];
      g[key].push(t);
    }
    return g;
  }, [tasks]);

  const handleDrop = (e, iso) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData("text/task-id");
    const dueIso = e.dataTransfer.getData("text/due-date");
    if (taskId) onDrop(taskId, iso, dueIso || null);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-0.5 border rounded-2xl overflow-hidden bg-white" data-testid="agenda-week-grid" style={{ borderColor: "var(--pxos-border)" }}>
      {days.map((d, i) => {
        const iso = isoDate(d);
        const list = byDay[iso] || [];
        const isToday = sameDay(d, today);
        return (
          <div key={iso}
               onDragOver={(e) => e.preventDefault()}
               onDrop={(e) => handleDrop(e, iso)}
               className="min-h-[240px] p-2 flex flex-col gap-1 border-r"
               style={{
                 borderColor: "var(--pxos-border)",
                 background: isToday ? "var(--pxos-primary-light)" : "white",
                 // Bordo evidenza giorno corrente (requisito Piera #6):
                 // non affidarsi al solo colore → un contorno esplicito è
                 // percepito anche da chi ha daltonismo.
                 boxShadow: isToday ? "inset 0 0 0 2px var(--pxos-primary)" : "none",
               }}
               aria-current={isToday ? "date" : undefined}
               data-testid="week-day-cell"
               data-today={isToday ? "true" : "false"}
               data-day-iso={iso}>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => onDayOpen && onDayOpen(iso)}
                className="text-left hover:bg-white/60 rounded-md px-1 py-0.5 transition"
                data-testid="week-day-header"
                title={`Apri "${DAY_LABELS_LONG[i]} ${d.getDate()} ${MONTHS_IT[d.getMonth()]}"`}
                aria-label={`Apri giornata ${DAY_LABELS_LONG[i]} ${d.getDate()}`}
              >
                <div className="text-[10px] uppercase font-mono opacity-70">
                  <span className="hidden md:inline">{DAY_LABELS_LONG[i]}</span>
                  <span className="md:hidden">{DAY_LABELS_SHORT[i]}</span>
                </div>
                <div className={`text-lg font-display font-semibold ${isToday ? "text-blue-800" : ""}`} data-testid="week-day-number">{d.getDate()}</div>
              </button>
              <button onClick={() => onAddClick(iso)} className="opacity-50 hover:opacity-100 p-1 rounded-full hover:bg-white"
                      title={`Aggiungi task per ${DAY_LABELS_LONG[i]} ${d.getDate()}`} data-testid="week-add-btn" aria-label="Aggiungi task">
                <Plus size={14}/>
              </button>
            </div>
            <div className="space-y-1 mt-1 flex-1">
              {list.length === 0 ? (
                <div className="text-[10px] opacity-40 pt-2">Vuoto</div>
              ) : list.map((t) => {
                const isDone = t.status === "completed" || t.status === "cancelled";
                return (
                  <div key={t._key || t.id} style={{ opacity: isDone ? 0.45 : 1, textDecoration: isDone ? "line-through" : "none" }} data-testid="week-task-slot">
                    <AgendaItem task={t} area={areaById[t.area_id]} onClick={onTaskClick} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Month View · calendario mese classico 6×7 ──────────────────
function MonthView({ refDate, tasks, areaById, today, onAddClick, onDrop, onDayOpen, onTaskClick }) {
  const year = refDate.getFullYear();
  const month = refDate.getMonth();
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDay = (first.getDay() + 6) % 7; // Monday=0
  const totalCells = 42;

  const byDay = useMemo(() => {
    const g = {};
    for (const t of tasks) {
      const key = t.scheduled_date || t.due_date;
      if (!key) continue;
      g[key] ??= [];
      g[key].push(t);
    }
    return g;
  }, [tasks]);

  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - startDay + 1;
    if (dayNum >= 1 && dayNum <= daysInMonth) {
      cells.push(new Date(year, month, dayNum));
    } else {
      const d = new Date(year, month, dayNum);
      cells.push({ date: d, offMonth: true });
    }
  }

  const handleDrop = (e, iso) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData("text/task-id");
    const dueIso = e.dataTransfer.getData("text/due-date");
    if (taskId) onDrop(taskId, iso, dueIso || null);
  };

  return (
    <div className="rounded-2xl border overflow-hidden bg-white" data-testid="agenda-month-grid" style={{ borderColor: "var(--pxos-border)" }}>
      <div className="grid grid-cols-7 border-b" style={{ borderColor: "var(--pxos-border)" }}>
        {DAY_LABELS_SHORT.map((w) => (
          <div key={w} className="text-center text-[10px] font-mono uppercase opacity-70 py-2">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((entry, i) => {
          const d = entry instanceof Date ? entry : entry.date;
          const off = !!(entry.offMonth);
          const iso = isoDate(d);
          const list = byDay[iso] || [];
          const isToday = sameDay(d, today);
          const visible = list.slice(0, 3);
          const extra = list.length - visible.length;
          return (
            <div key={i}
                 onDragOver={(e) => e.preventDefault()}
                 onDrop={(e) => handleDrop(e, iso)}
                 className={`min-h-[92px] p-1.5 border-r border-b relative ${off ? "opacity-40" : "hover:bg-black/[0.02]"}`}
                 style={{
                   borderColor: "var(--pxos-border)",
                   background: isToday ? "var(--pxos-primary-light)" : "white",
                   boxShadow: isToday ? "inset 0 0 0 2px var(--pxos-primary)" : "none",
                 }}
                 aria-current={isToday ? "date" : undefined}
                 data-testid="month-day-cell"
                 data-today={isToday ? "true" : "false"}
                 data-day-iso={iso}>
              <div className="flex items-start justify-between">
                <button
                  onClick={() => !off && onDayOpen(iso)}
                  className={`text-sm font-display ${isToday ? "font-bold text-blue-800" : ""}`}
                  title={off ? "" : "Apri la giornata"}
                  data-testid="month-day-number"
                  aria-label={off ? undefined : "Apri giornata"}>
                  {d.getDate()}
                </button>
                {!off && (
                  <button onClick={(e) => { e.stopPropagation(); onAddClick(iso); }}
                          className="opacity-40 hover:opacity-100 p-0.5" data-testid="month-add-btn"
                          title="Aggiungi ciclo in questa giornata" aria-label="Aggiungi ciclo">
                    <Plus size={12}/>
                  </button>
                )}
              </div>
              <div className="mt-0.5 space-y-0.5">
                {visible.map((t) => {
                  const isDone = t.status === "completed" || t.status === "cancelled";
                  return (
                    <div key={t._key || t.id} style={{ opacity: isDone ? 0.45 : 1, textDecoration: isDone ? "line-through" : "none" }} data-testid="month-task-slot">
                      <AgendaItem task={t} area={areaById[t.area_id]} size="sm" onClick={onTaskClick} showTime={false} />
                    </div>
                  );
                })}
                {extra > 0 && (
                  <button className="text-[10px] opacity-60 hover:opacity-90 underline cursor-pointer"
                          onClick={() => onDayOpen(iso)} data-testid="month-extra-count"
                          title="Apri la giornata per vedere tutti i cicli">
                    +{extra} altre
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Year View · 12 mini-calendari ──────────────────────────────
function YearView({ heat, year, today, onDay, onMonth }) {
  const months = Array.from({ length: 12 }, (_, i) => i);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="agenda-year-grid">
      {months.map((m) => (
        <MiniCal key={m} year={year} month={m} today={today} heat={heat?.days || {}}
                 onDay={onDay} onMonth={() => onMonth(m + 1)} />
      ))}
    </div>
  );
}

function MiniCal({ year, month, today, heat, onDay, onMonth }) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDay = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthLabel = first.toLocaleDateString("it-IT", { month: "long" });
  const isCurrentMonth = today && today.getFullYear() === year && today.getMonth() === month;
  return (
    <div
      className="pxos-card p-3"
      style={isCurrentMonth ? { boxShadow: "inset 0 0 0 2px var(--pxos-primary)" } : {}}
      data-current-month={isCurrentMonth ? "true" : "false"}
    >
      <button className="w-full text-left mb-2" onClick={onMonth} data-testid="year-mini-month" aria-current={isCurrentMonth ? "date" : undefined}>
        <div className="font-display text-base font-semibold capitalize">
          {monthLabel}
          {isCurrentMonth && <span className="ml-2 text-[10px] uppercase tracking-widest opacity-60">Oggi</span>}
        </div>
      </button>
      <div className="grid grid-cols-7 gap-0.5 text-[10px] font-mono opacity-60 text-center mb-1">
        {DAY_LABELS_SHORT.map((w, i) => <div key={i}>{w[0]}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-xs">
        {cells.map((d, i) => {
          if (!d) return <div key={i}></div>;
          const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const info = heat[iso];
          const bg = info?.color || null;
          const isTodayCell = today && today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;
          return (
            <button key={i} onClick={() => onDay(iso)}
                    title={info ? info.titles.join("\n") : ""}
                    className={`aspect-square rounded-md text-[11px] hover:ring-1 ring-black/20 grid place-items-center ${info ? "font-semibold" : "opacity-70"}`}
                    style={{
                      ...(info ? { background: bg + "cc", color: "#20344B" } : {}),
                      ...(isTodayCell ? { boxShadow: "inset 0 0 0 2px var(--pxos-primary)" } : {}),
                    }}
                    aria-current={isTodayCell ? "date" : undefined}
                    data-testid="year-day-cell">
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}
